import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ShieldCheck,
  X,
  ChevronDown,
  Lock,
  Sliders,
  BarChart3,
  Gauge,
  Megaphone,
} from "lucide-react";
import { Switch } from "./ui/switch";

/**
 * PrimeS4 AI Privacy & Cookie Preferences.
 *
 * Renders a small floating shield button in the bottom-right corner. Clicking
 * opens an enterprise-style modal where the visitor can accept / reject /
 * fine-tune consent across categories. Preferences are persisted to
 * localStorage under a versioned key so future schema bumps are safe.
 *
 * All consent state lives here in the frontend — no backend or analytics
 * hook is wired up yet, which keeps existing compliance behaviour intact.
 */
const STORAGE_KEY = "primes4.privacy.consent.v1";

const CATEGORIES = [
  {
    id: "necessary",
    icon: Lock,
    title: "Necessary",
    summary: "Required for the platform to operate securely.",
    body: "Necessary cookies enable core platform functionality such as authenticated sessions, security controls, load balancing, and saving your preference choices. These cannot be turned off.",
    required: true,
  },
  {
    id: "functional",
    icon: Sliders,
    title: "Functional",
    summary: "Enhances usability and remembers your in-product choices.",
    body: "Functional cookies remember UI preferences, regional settings, and feature toggles. Disabling them may reduce personalisation across PrimeS4 AI applications.",
    required: false,
  },
  {
    id: "analytics",
    icon: BarChart3,
    title: "Analytics",
    summary: "Helps us understand product usage and improve experiences.",
    body: "Analytics cookies provide aggregated, anonymised usage signals — pages visited, feature adoption, and navigation paths — that help us refine PrimeS4 AI platforms.",
    required: false,
  },
  {
    id: "performance",
    icon: Gauge,
    title: "Performance",
    summary: "Measures reliability and operational performance.",
    body: "Performance cookies collect technical metrics such as page-load timing, API latency, and error rates so we can keep the platform fast and stable.",
    required: false,
  },
  {
    id: "marketing",
    icon: Megaphone,
    title: "Marketing",
    summary: "Used for relevant content and campaign measurement.",
    body: "Marketing cookies may be set by PrimeS4 AI or trusted partners to measure campaign effectiveness and present relevant enterprise content.",
    required: false,
  },
];

const defaultPrefs = () =>
  CATEGORIES.reduce(
    (acc, c) => ({ ...acc, [c.id]: c.required ? true : false }),
    {}
  );

const readStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.prefs === "object") return parsed;
  } catch {
    /* ignore */
  }
  return null;
};

const writeStored = (prefs) => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ prefs, updated_at: Date.now() })
    );
  } catch {
    /* ignore */
  }
};

export const PrivacyConsent = () => {
  const stored = useMemo(() => readStored(), []);
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(() => stored?.prefs || defaultPrefs());
  const [expanded, setExpanded] = useState(null);

  // Hide the floating consent icon for authenticated users. Marketing /
  // public visitors still see it; logged-in admins, consultants, demo,
  // and client portal users do not. We poll on a light interval so the
  // icon disappears as soon as the user lands on a post-login route
  // without forcing a hard refresh.
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const ping = async () => {
      try {
        const r = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/admin/auth/me`,
          { withCredentials: true, validateStatus: () => true },
        );
        if (cancelled) return;
        setIsAuthed(r.status === 200);
      } catch {
        if (!cancelled) setIsAuthed(false);
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    };
    ping();
    const id = setInterval(ping, 15000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const persist = (next) => {
    setPrefs(next);
    writeStored(next);
  };

  const acceptAll = () => {
    const next = CATEGORIES.reduce((acc, c) => ({ ...acc, [c.id]: true }), {});
    persist(next);
    setOpen(false);
  };
  const rejectNonEssential = () => {
    persist(defaultPrefs());
    setOpen(false);
  };
  const saveChoices = () => {
    persist(prefs);
    setOpen(false);
  };

  // Hide entirely once we've confirmed the visitor is authenticated.
  if (authChecked && isAuthed) return null;

  return (
    <>
      {/* Floating trigger — bottom-right (clears the left sidebar footer links) */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Privacy & cookie preferences"
        data-testid="privacy-trigger"
        className="fixed z-40 right-4 bottom-4 sm:right-6 sm:bottom-6 w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center bg-white text-[#025979] border border-[#BAE6FD] transition-all duration-300 hover:border-[#0093cb] hover:text-[#03738C] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0093cb]"
        style={{
          boxShadow:
            "0 8px 22px -8px rgba(2,89,121,0.35), 0 2px 6px -2px rgba(15,23,42,0.18)",
        }}
      >
        <ShieldCheck className="w-5 h-5" strokeWidth={1.75} />
      </button>

      {/* Modal */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="privacy-modal-title"
          data-testid="privacy-modal"
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center px-3 sm:px-6 py-4 sm:py-8"
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label="Close privacy preferences"
            data-testid="privacy-backdrop"
            onClick={() => setOpen(false)}
            className="absolute inset-0 bg-[#0F172A]/55 backdrop-blur-[2px]"
          />

          {/* Card */}
          <div
            className="relative w-full max-w-2xl max-h-[90vh] flex flex-col rounded-md bg-white border border-[#E5E7EB] shadow-2xl"
            data-testid="privacy-modal-card"
          >
            {/* Accent strip */}
            <div
              className="h-1 rounded-t-md shrink-0"
              style={{
                background:
                  "linear-gradient(90deg, #0093cb 0%, #7C3AED 50%, #0093cb 100%)",
              }}
            />

            {/* Header */}
            <div className="px-6 py-5 sm:px-7 sm:py-6 border-b border-[#F1F5F9] flex items-start gap-4 shrink-0">
              <div
                className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0"
                style={{
                  background:
                    "linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)",
                }}
              >
                <ShieldCheck
                  className="w-5 h-5 text-[#025979]"
                  strokeWidth={1.7}
                />
              </div>
              <div className="flex-1 min-w-0">
                <h2
                  id="privacy-modal-title"
                  data-testid="privacy-modal-title"
                  className="text-[18px] sm:text-[20px] font-bold text-[#0F172A] tracking-tight leading-tight"
                  style={{ fontFamily: "Chivo, sans-serif" }}
                >
                  Privacy &amp; Cookie Preferences
                </h2>
                <p className="mt-2 text-[13px] text-[#475569] leading-relaxed">
                  We use cookies and related technologies to improve platform performance, enhance user experience, and support analytics and operational functionality.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                data-testid="privacy-modal-close"
                className="shrink-0 w-8 h-8 rounded-sm flex items-center justify-center text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A] transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Categories list */}
            <div
              className="flex-1 overflow-y-auto px-3 sm:px-4 py-3 sm:py-4"
              data-testid="privacy-categories"
            >
              {CATEGORIES.map((c) => {
                const isOpen = expanded === c.id;
                const Icon = c.icon;
                return (
                  <div
                    key={c.id}
                    data-testid={`privacy-category-${c.id}`}
                    className="border-b border-[#F1F5F9] last:border-b-0"
                  >
                    <div className="flex items-center gap-3 sm:gap-4 px-3 py-3.5">
                      <button
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : c.id)}
                        aria-expanded={isOpen}
                        data-testid={`privacy-category-toggle-${c.id}`}
                        className="flex-1 flex items-center gap-3 text-left min-w-0 focus:outline-none"
                      >
                        <span
                          className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0"
                          style={{
                            background:
                              "linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)",
                          }}
                        >
                          <Icon
                            className="w-3.5 h-3.5 text-[#025979]"
                            strokeWidth={1.7}
                          />
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-[14px] font-semibold text-[#0F172A] leading-tight">
                            {c.title}
                          </p>
                          <p className="mt-0.5 text-[12px] text-[#64748B] leading-snug truncate">
                            {c.summary}
                          </p>
                        </div>
                        <ChevronDown
                          className={`w-4 h-4 text-[#94A3B8] transition-transform shrink-0 ${
                            isOpen ? "rotate-180" : ""
                          }`}
                          strokeWidth={1.8}
                        />
                      </button>
                      {c.required ? (
                        <span
                          className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#025979] px-2 py-1 rounded-sm bg-[#E0F2FE] shrink-0"
                          data-testid={`privacy-category-required-${c.id}`}
                        >
                          Always
                        </span>
                      ) : (
                        <Switch
                          checked={!!prefs[c.id]}
                          onCheckedChange={(v) =>
                            setPrefs((p) => ({ ...p, [c.id]: v }))
                          }
                          aria-label={`Toggle ${c.title} cookies`}
                          data-testid={`privacy-category-switch-${c.id}`}
                          className="data-[state=checked]:bg-[#0093cb]"
                        />
                      )}
                    </div>
                    {isOpen && (
                      <p
                        className="px-3 pb-4 pl-14 text-[12.5px] text-[#475569] leading-relaxed"
                        data-testid={`privacy-category-body-${c.id}`}
                      >
                        {c.body}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer actions */}
            <div
              className="px-5 sm:px-7 py-4 sm:py-5 border-t border-[#F1F5F9] bg-[#FAFAFF] rounded-b-md shrink-0 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2 sm:gap-3"
              data-testid="privacy-modal-actions"
            >
              <button
                type="button"
                onClick={rejectNonEssential}
                data-testid="privacy-reject"
                className="text-[12.5px] font-semibold text-[#475569] hover:text-[#0F172A] px-2.5 py-2 rounded-sm transition-colors"
              >
                Reject Non-Essential
              </button>
              <button
                type="button"
                onClick={saveChoices}
                data-testid="privacy-manage"
                className="inline-flex items-center justify-center gap-2 rounded-sm h-10 px-5 text-[12.5px] font-semibold text-[#025979] border border-[#BAE6FD] bg-white hover:bg-[#E0F2FE] hover:border-[#0093cb] transition-colors"
              >
                Manage Preferences
              </button>
              <button
                type="button"
                onClick={acceptAll}
                data-testid="privacy-accept-all"
                className="inline-flex items-center justify-center gap-2 rounded-sm h-10 px-5 text-[12.5px] font-semibold text-white transition-colors"
                style={{ backgroundColor: "#0093cb" }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#03738C")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#0093cb")
                }
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PrivacyConsent;
